import { DollarSign, Package, Factory, Users, ArrowUpRight } from 'lucide-react';
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis,
  BarChart, Bar, CartesianGrid,
} from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';

// Placeholder data — replaced by live queries to Firestore/API once
// the reporting service ships.
const revenueData = [
  { m: 'Jan', v: 420 }, { m: 'Feb', v: 465 }, { m: 'Mar', v: 512 },
  { m: 'Apr', v: 498 }, { m: 'May', v: 560 }, { m: 'Jun', v: 612 },
  { m: 'Jul', v: 598 }, { m: 'Aug', v: 641 }, { m: 'Sep', v: 685 },
  { m: 'Oct', v: 720 }, { m: 'Nov', v: 742 }, { m: 'Dec', v: 778 },
];

const productionData = [
  { d: 'Mon', panels: 145, doors: 62 },
  { d: 'Tue', panels: 168, doors: 71 },
  { d: 'Wed', panels: 152, doors: 58 },
  { d: 'Thu', panels: 181, doors: 74 },
  { d: 'Fri', panels: 165, doors: 69 },
];

const recentOrders = [
  { id: 'WO-2604',  client: 'Gensler — Downtown LA',    status: 'production',  amount: 128_400, due: 'May 02' },
  { id: 'WO-2603',  client: 'HOK — San Francisco',      status: 'shipped',     amount:  86_200, due: 'Apr 28' },
  { id: 'WO-2602',  client: 'Skidmore Owings & Merrill', status: 'pending',    amount: 215_900, due: 'May 14' },
  { id: 'WO-2601',  client: 'Perkins & Will',            status: 'production', amount:  94_500, due: 'May 08' },
  { id: 'WO-2600',  client: 'Gensler — Seattle',         status: 'shipped',    amount:  62_100, due: 'Apr 22' },
];

const statusTone = {
  production: 'warning',
  shipped:    'success',
  pending:    'neutral',
} as const;

export function DashboardPage() {
  const { user } = useAuth();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.displayName?.split(' ')[0] ?? '';

  return (
    <div className="space-y-10">
      {/* Hero */}
      <SectionHeader
        kicker={`${greeting}${firstName ? ', ' + firstName : ''}`}
        title="Operations at a glance."
        description="Real-time pulse across financials, production, inventory and clients. Data refreshes every 60 seconds."
        action={
          <Button variant="secondary" rightIcon={<ArrowUpRight size={14} />}>
            Export Summary
          </Button>
        }
      />

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Revenue · MTD"
          value={formatCurrency(742_380)}
          delta={12.4}
          deltaLabel="vs last month"
          icon={DollarSign}
          accent
        />
        <KpiCard
          label="Active Orders"
          value="38"
          delta={6.2}
          deltaLabel="vs last week"
          icon={Factory}
        />
        <KpiCard
          label="Stock Value"
          value={formatCurrency(2_140_000)}
          delta={-3.1}
          deltaLabel="vs last month"
          icon={Package}
        />
        <KpiCard
          label="New Clients · YTD"
          value="27"
          delta={18.0}
          deltaLabel="vs last year"
          icon={Users}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue */}
        <Card className="lg:col-span-2">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="kicker mb-1">Revenue · 12 Months</div>
              <h3 className="font-display text-2xl text-text">
                {formatCurrency(revenueData.reduce((a, x) => a + x.v, 0) * 1000)}
              </h3>
            </div>
            <Badge tone="brass">Trailing</Badge>
          </div>

          <div className="h-72 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#cc9d34" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#cc9d34" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2c2720" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="m" stroke="#8f826f" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#8f826f" fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => `$${v}k`} />
                <Tooltip
                  contentStyle={{
                    background: '#1a1713', border: '1px solid #413930',
                    borderRadius: 2, fontSize: 12, color: '#f0e6d6',
                  }}
                  labelStyle={{ color: '#b4a897', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                  formatter={(v: number) => [`$${v}k`, 'Revenue']}
                />
                <Area type="monotone" dataKey="v" stroke="#cc9d34" strokeWidth={1.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Production */}
        <Card>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="kicker mb-1">Production · This Week</div>
              <h3 className="font-display text-2xl text-text tabular">
                {productionData.reduce((a, x) => a + x.panels + x.doors, 0)}
                <span className="text-text-dim text-sm ml-1 font-sans">units</span>
              </h3>
            </div>
          </div>

          <div className="h-72 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={productionData} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                <CartesianGrid stroke="#2c2720" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="d" stroke="#8f826f" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#8f826f" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: '#211d18' }}
                  contentStyle={{
                    background: '#1a1713', border: '1px solid #413930',
                    borderRadius: 2, fontSize: 12, color: '#f0e6d6',
                  }}
                  labelStyle={{ color: '#b4a897', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                />
                <Bar dataKey="panels" stackId="a" fill="#cc9d34" />
                <Bar dataKey="doors"  stackId="a" fill="#85572e" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center gap-4 mt-4 text-xs">
            <span className="flex items-center gap-2 text-text-muted">
              <span className="h-2 w-2 rounded-sm bg-brass-400" /> Panels
            </span>
            <span className="flex items-center gap-2 text-text-muted">
              <span className="h-2 w-2 rounded-sm bg-walnut-700" /> Doors
            </span>
          </div>
        </Card>
      </div>

      {/* Recent orders */}
      <div>
        <SectionHeader
          kicker="Workflow"
          title="Recent Work Orders"
          action={<Button variant="ghost" size="sm">View All →</Button>}
        />
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Order</th>
                <th className="kicker px-6 py-3 font-normal">Client</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal text-right">Amount</th>
                <th className="kicker px-6 py-3 font-normal">Due</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((o) => (
                <tr key={o.id} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors">
                  <td className="px-6 py-4 font-mono text-brass-300">{o.id}</td>
                  <td className="px-6 py-4 text-text">{o.client}</td>
                  <td className="px-6 py-4">
                    <Badge tone={statusTone[o.status as keyof typeof statusTone]}>
                      {o.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right tabular text-text">
                    {formatCurrency(o.amount)}
                  </td>
                  <td className="px-6 py-4 text-text-muted tabular">{o.due}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
